// @ts-nocheck
/* eslint-disable no-param-reassign */
import axios from 'axios';
import { create } from 'zustand';
import { API_URL } from '../../configs';
import getRoot from '../root';

/**
 * Standard OAuth-style integration store. Used by HubSpot, Salesforce,
 * Mailchimp, Fanbridge, Pipedrive, SalesforceMarketingCloud — all share the
 * same get/update configs + getAuthUrl/getTokens shape.
 *
 * `partnerKey` is the `/api/integrations/{partnerKey}/…` URL segment AND the
 * field on `root.integrations` (matches `useFooStore` reads in components).
 *
 * `toastKeys.update` / `toastKeys.tokens` are the toastType IDs to dedupe by.
 */
export const createOAuthIntegrationStore = ({
  partnerKey,
  defaultConfigs = { enabled: false },
  toastKeys = {},
  successMessages = {},
}) => create((set, get) => ({
  configs: defaultConfigs,
  loading: false,

  reset: () => set({ configs: {}, loading: false }),

  setConfigField: (name, value) => set((s) => ({ configs: { ...s.configs, [name]: value } })),

  getAuthUrl: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/${partnerKey}/auth-url`);
      if (res.data.success) {
        window.open(res.data.authUrl, '_blank');
        return res.data.authUrl;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  },

  getConfigs: async () => {
    try {
      set({ loading: true });
      const { data } = await axios.get(`${API_URL}/api/integrations/${partnerKey}/config`);
      if (data.success) set((s) => ({ configs: { ...s.configs, ...data.config } }));
    } catch (e) {
      // ignore
    } finally {
      set({ loading: false });
    }
  },

  updateConfigs: async () => {
    const root = getRoot();
    try {
      set({ loading: true });
      const { data } = await axios.post(`${API_URL}/api/integrations/${partnerKey}/config`, {
        ...get().configs,
      });
      if (data.success) {
        root.success(successMessages.update || 'Successfully updated the configurations.', toastKeys.update);
        root.clearToast(toastKeys.update);
      } else {
        throw new Error();
      }
    } catch (e) {
      root.failure('Something went wrong...', toastKeys.update);
    } finally {
      get().getConfigs();
      set({ loading: false });
    }
  },

  getTokens: async (code) => {
    const root = getRoot();
    try {
      set({ loading: true });
      const { data } = await axios.get(`${API_URL}/api/integrations/${partnerKey}/get-tokens?code=${code}`);
      if (data.success) {
        root.success(successMessages.tokens || `${partnerKey} integration successful.`, toastKeys.tokens);
        root.clearToast(toastKeys.tokens);
      } else {
        throw new Error();
      }
    } catch (e) {
      root.failure(`${partnerKey} integration unsuccessful.`, toastKeys.tokens);
    } finally {
      set({ loading: false });
    }
  },
}));

/**
 * Credentials-style integration store. Used by OMI366 (extended) and Blitz —
 * connection is via key/secret form fields rather than OAuth.
 */
export const createCredentialsIntegrationStore = ({
  partnerKey,
  defaultConfigs = { enabled: false },
  toastKeys = {},
}) => create((set, get) => ({
  configs: defaultConfigs,
  loading: false,

  reset: () => set({ configs: {}, loading: false }),

  setConfigField: (name, value) => set((s) => ({ configs: { ...s.configs, [name]: value } })),

  getConfigs: async () => {
    try {
      set({ loading: true });
      const { data } = await axios.get(`${API_URL}/api/integrations/${partnerKey}/config`);
      if (data.success) set((s) => ({ configs: { ...s.configs, ...data.config } }));
    } catch (e) {
      // ignore
    } finally {
      set({ loading: false });
    }
  },

  updateConfigs: async () => {
    const root = getRoot();
    try {
      set({ loading: true });
      const { data } = await axios.post(`${API_URL}/api/integrations/${partnerKey}/config`, {
        ...get().configs,
      });
      if (data.success) {
        root.success('Successfully updated the configurations.', toastKeys.update);
        root.clearToast(toastKeys.update);
      } else {
        throw new Error();
      }
    } catch (e) {
      root.failure('Something went wrong...', toastKeys.update);
    } finally {
      get().getConfigs();
      set({ loading: false });
    }
  },
}));
